import {
  API_CUSTOM_FIELDS,
  API_ROOM_BOOKINGS,
  API_SESSION_ORDERS,
  API_SESSIONS,
  API_TRAINING_WISHES,
  API_TRIGGER_EMAILS_USERS,
} from 'constants/api-v2'
import type QueryClient from 'services/query'
import { buildURL } from 'services/url'
import type { RoomBooking } from 'shared/types'

interface LoaderParams {
  params: {
    id?: string
    tab?: string
  }
}

export const loadSession =
  (client: QueryClient) =>
  async ({ params }: LoaderParams) => {
    const { id, tab } = params
    let session = null

    const url = buildURL(
      `${API_SESSIONS}/${id}`,
      'groups[]=sessions:view:details',
      'groups[]=sessions:view:form',
      tab === 'infos' && 'groups[]=sessions:participant-informations:view',
    )

    try {
      session = await client.get(url)
    } catch (e: any) {
      if (e?.response?.status === 404) {
        throw new Response('', { status: 404 })
      }
    }
    return session
  }

export const loadSessionPeriodForm =
  (client: QueryClient) =>
  async ({ params }: LoaderParams) => {
    const { id } = params

    let session = null

    try {
      session = await client.get(`${API_SESSIONS}/${id}?groups[]=sessions:view:period-form`)
    } catch (e: any) {
      if (e?.response?.status === 404) {
        throw new Response('', { status: 404 })
      }
    }
    return session
  }

export const loadCustomFieldsAndBooking =
  (client: QueryClient) =>
  async ({ request }: { request: Request }) => {
    let customFields = []
    let booking: RoomBooking | null = null

    // @see https://gitlab.com/eleo-rh/training/-/issues/10025
    const { searchParams } = new URL(request.url)
    const bookingId = searchParams.has('bookingId') ? searchParams.get('bookingId') : null

    try {
      ;[customFields, booking] = await Promise.all([
        client.get(API_CUSTOM_FIELDS),
        bookingId ? client.get(`${API_ROOM_BOOKINGS}/${bookingId}`) : Promise.resolve(null),
      ])
    } catch {
      // pass
    }

    return {
      customFields: customFields.items ?? customFields,
      booking,
    }
  }

export const loadSessionAndCustomFields =
  (client: QueryClient) =>
  async ({ params }: LoaderParams) => {
    const { id } = params

    let session = null
    let customFields = []

    try {
      ;[session, customFields] = await Promise.all([
        client.get(`${API_SESSIONS}/${id}?groups[]=sessions:view:form`),
        client.get(API_CUSTOM_FIELDS),
      ])
    } catch (e: any) {
      if (e?.response?.status === 404) {
        throw new Response('', { status: 404 })
      }
    }

    return { session, customFields: customFields?.items }
  }

export const loadTab =
  (client: QueryClient) =>
  async ({ params }: LoaderParams) => {
    const { id, tab } = params

    const trainingWishes = await client.get(
      // eslint-disable-next-line max-len
      `${API_TRAINING_WISHES}?groups[]=training-wishes:list:sessions&session=${id}&pagination=false&order[user.fullName]=ASC`,
    )

    let data
    let error = false

    switch (tab) {
      case 'mails':
        data = []
        try {
          data = await client.get(`${API_TRIGGER_EMAILS_USERS}?trainingWish.session=${id}`)
        } catch {
          error = true
        }
        break
      case 'surveys':
        data = []
        try {
          data = await client.get(`${API_TRIGGER_EMAILS_USERS}?trainingWish.session=${id}`)
        } catch {
          error = true
        }
        break
      case 'orders':
        data = []
        try {
          const response = await client.get(`${API_SESSION_ORDERS}?session=${id}`)
          data = response.items
        } catch {
          error = true
        }
        break
      default:
      // pass
    }

    return {
      error,
      data,
      trainingWishes,
    }
  }
